import React, { useState, useEffect } from 'react';
import { Save } from 'lucide-react';
import ModalContainer from '../../../components/ui/ModalContainer';
import Button from '../../../components/ui/Button';
import { projectTemplateService } from '../../../services/projectTemplateService';

const TemplateFormModal = ({ isOpen, onClose, onSuccess, template }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    templateName: '',
    projectType: '',
    description: '',
    defaultTotalObra: '',
    defaultValorJaFaturado: '',
    defaultCustoEmpreiteiro: '',
    defaultEquipeMensalPrestadores: '',
    defaultVisitasObraTotal: '',
    defaultOutrosCustosDiretos: '',
    defaultFixosMensaisEscritorio: '',
    defaultDuracaoSemanas: '',
    defaultDuracaoMeses: '',
    defaultDasPercentualEfetivo: '6.0',
    defaultDasBaseCalculo: 'total_obra',
    defaultProlaboreMensal: '5000.00',
    defaultInssPercentualSeg: '11.0'
  });

  useEffect(() => {
    if (template) {
      setFormData({
        templateName: template?.templateName || '',
        projectType: template?.projectType || '',
        description: template?.description || '',
        defaultTotalObra: template?.defaultTotalObra?.toString() || '',
        defaultValorJaFaturado: template?.defaultValorJaFaturado?.toString() || '',
        defaultCustoEmpreiteiro: template?.defaultCustoEmpreiteiro?.toString() || '',
        defaultEquipeMensalPrestadores: template?.defaultEquipeMensalPrestadores?.toString() || '',
        defaultVisitasObraTotal: template?.defaultVisitasObraTotal?.toString() || '',
        defaultOutrosCustosDiretos: template?.defaultOutrosCustosDiretos?.toString() || '',
        defaultFixosMensaisEscritorio: template?.defaultFixosMensaisEscritorio?.toString() || '',
        defaultDuracaoSemanas: template?.defaultDuracaoSemanas?.toString() || '',
        defaultDuracaoMeses: template?.defaultDuracaoMeses?.toString() || '',
        defaultDasPercentualEfetivo: template?.defaultDasPercentualEfetivo?.toString() || '6.0',
        defaultDasBaseCalculo: template?.defaultDasBaseCalculo || 'total_obra',
        defaultProlaboreMensal: template?.defaultProlaboreMensal?.toString() || '5000.00',
        defaultInssPercentualSeg: template?.defaultInssPercentualSeg?.toString() || '11.0'
      });
    } else {
      setFormData({
        templateName: '',
        projectType: '',
        description: '',
        defaultTotalObra: '',
        defaultValorJaFaturado: '',
        defaultCustoEmpreiteiro: '',
        defaultEquipeMensalPrestadores: '',
        defaultVisitasObraTotal: '',
        defaultOutrosCustosDiretos: '',
        defaultFixosMensaisEscritorio: '',
        defaultDuracaoSemanas: '',
        defaultDuracaoMeses: '',
        defaultDasPercentualEfetivo: '6.0',
        defaultDasBaseCalculo: 'total_obra',
        defaultProlaboreMensal: '5000.00',
        defaultInssPercentualSeg: '11.0'
      });
    }
    setError('');
  }, [template, isOpen]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Auto-calculate duracaoMeses when duracaoSemanas changes
    if (field === 'defaultDuracaoSemanas' && value) {
      const meses = Math.ceil(parseFloat(value) / 4.33);
      setFormData(prev => ({ ...prev, defaultDuracaoMeses: meses?.toString() }));
    }
  };

  const validateForm = () => {
    if (!formData?.templateName?.trim()) {
      setError('Nome do template é obrigatório');
      return false;
    }
    if (!formData?.projectType?.trim()) {
      setError('Tipo de projeto é obrigatório');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');

    if (!validateForm()) return;

    try {
      setLoading(true);
      
      const templateData = {
        templateName: formData?.templateName,
        projectType: formData?.projectType,
        description: formData?.description,
        defaultTotalObra: formData?.defaultTotalObra ? parseFloat(formData?.defaultTotalObra) : null,
        defaultValorJaFaturado: formData?.defaultValorJaFaturado ? parseFloat(formData?.defaultValorJaFaturado) : 0,
        defaultCustoEmpreiteiro: formData?.defaultCustoEmpreiteiro ? parseFloat(formData?.defaultCustoEmpreiteiro) : 0,
        defaultEquipeMensalPrestadores: formData?.defaultEquipeMensalPrestadores ? parseFloat(formData?.defaultEquipeMensalPrestadores) : 0,
        defaultVisitasObraTotal: formData?.defaultVisitasObraTotal ? parseFloat(formData?.defaultVisitasObraTotal) : 0,
        defaultOutrosCustosDiretos: formData?.defaultOutrosCustosDiretos ? parseFloat(formData?.defaultOutrosCustosDiretos) : 0,
        defaultFixosMensaisEscritorio: formData?.defaultFixosMensaisEscritorio ? parseFloat(formData?.defaultFixosMensaisEscritorio) : 0,
        defaultDuracaoSemanas: formData?.defaultDuracaoSemanas ? parseInt(formData?.defaultDuracaoSemanas) : null,
        defaultDuracaoMeses: formData?.defaultDuracaoMeses ? parseInt(formData?.defaultDuracaoMeses) : null,
        defaultDasPercentualEfetivo: parseFloat(formData?.defaultDasPercentualEfetivo),
        defaultDasBaseCalculo: formData?.defaultDasBaseCalculo,
        defaultProlaboreMensal: parseFloat(formData?.defaultProlaboreMensal),
        defaultInssPercentualSeg: parseFloat(formData?.defaultInssPercentualSeg)
      };

      if (template) {
        await projectTemplateService?.updateTemplate(template?.id, templateData);
      } else {
        await projectTemplateService?.createTemplate(templateData);
      }

      onSuccess?.();
    } catch (err) {
      setError('Erro ao salvar template: ' + err?.message);
      console.error('Erro ao salvar:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <ModalContainer
      isOpen={isOpen}
      onClose={onClose}
      title={template ? 'Editar Template' : 'Criar Novo Template'}
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        {error && (
          <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg text-sm text-destructive">
            {error}
          </div>
        )}

        {/* Basic Information */}
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-foreground">Informações Básicas</h3>
          
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              Nome do Template *
            </label>
            <input
              type="text"
              value={formData?.templateName}
              onChange={(e) => handleInputChange('templateName', e?.target?.value)}
              className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="Ex: Pequeno Projeto Residencial"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              Tipo de Projeto *
            </label>
            <input
              type="text"
              value={formData?.projectType}
              onChange={(e) => handleInputChange('projectType', e?.target?.value)}
              className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="Ex: Residencial, Comercial, Industrial"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              Descrição
            </label>
            <textarea
              value={formData?.description}
              onChange={(e) => handleInputChange('description', e?.target?.value)}
              rows={3}
              className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="Descreva o tipo de projeto e casos de uso..."
            />
          </div>
        </div>

        {/* Revenue Parameters */}
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-foreground">Receita Padrão</h3>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Valor Total da Obra (R$)
              </label>
              <input
                type="number"
                step="0.01"
                value={formData?.defaultTotalObra}
                onChange={(e) => handleInputChange('defaultTotalObra', e?.target?.value)}
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Valor Já Faturado (R$)
              </label>
              <input
                type="number"
                step="0.01"
                value={formData?.defaultValorJaFaturado}
                onChange={(e) => handleInputChange('defaultValorJaFaturado', e?.target?.value)}
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
          </div>
        </div>

        {/* Direct Costs */}
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-foreground">Custos Diretos Padrão</h3>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Custo Empreiteiro (R$)
              </label>
              <input
                type="number"
                step="0.01"
                value={formData?.defaultCustoEmpreiteiro}
                onChange={(e) => handleInputChange('defaultCustoEmpreiteiro', e?.target?.value)}
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Equipe Mensal (R$)
              </label>
              <input
                type="number"
                step="0.01"
                value={formData?.defaultEquipeMensalPrestadores}
                onChange={(e) => handleInputChange('defaultEquipeMensalPrestadores', e?.target?.value)}
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Visitas à Obra (R$)
              </label>
              <input
                type="number"
                step="0.01"
                value={formData?.defaultVisitasObraTotal}
                onChange={(e) => handleInputChange('defaultVisitasObraTotal', e?.target?.value)}
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Outros Custos Diretos (R$)
              </label>
              <input
                type="number"
                step="0.01"
                value={formData?.defaultOutrosCustosDiretos}
                onChange={(e) => handleInputChange('defaultOutrosCustosDiretos', e?.target?.value)}
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
          </div>
        </div>

        {/* Fixed Costs and Duration */}
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-foreground">Custos Fixos e Duração</h3>
          
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Fixos Mensais (R$)
              </label>
              <input
                type="number"
                step="0.01"
                value={formData?.defaultFixosMensaisEscritorio}
                onChange={(e) => handleInputChange('defaultFixosMensaisEscritorio', e?.target?.value)}
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Duração (Semanas)
              </label>
              <input
                type="number"
                value={formData?.defaultDuracaoSemanas}
                onChange={(e) => handleInputChange('defaultDuracaoSemanas', e?.target?.value)}
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Duração (Meses)
              </label>
              <input
                type="number"
                value={formData?.defaultDuracaoMeses}
                onChange={(e) => handleInputChange('defaultDuracaoMeses', e?.target?.value)}
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
          </div>
        </div>

        {/* Tax Settings */}
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-foreground">Configurações de Impostos</h3>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                DAS (%)
              </label>
              <input
                type="number"
                step="0.01"
                value={formData?.defaultDasPercentualEfetivo}
                onChange={(e) => handleInputChange('defaultDasPercentualEfetivo', e?.target?.value)}
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Base de Cálculo DAS
              </label>
              <select
                value={formData?.defaultDasBaseCalculo}
                onChange={(e) => handleInputChange('defaultDasBaseCalculo', e?.target?.value)}
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="total_obra">Total da Obra</option>
                <option value="valor_a_faturar">Valor a Faturar</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Pró-labore Mensal (R$)
              </label>
              <input
                type="number"
                step="0.01"
                value={formData?.defaultProlaboreMensal}
                onChange={(e) => handleInputChange('defaultProlaboreMensal', e?.target?.value)}
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                INSS (%)
              </label>
              <input
                type="number"
                step="0.01"
                value={formData?.defaultInssPercentualSeg}
                onChange={(e) => handleInputChange('defaultInssPercentualSeg', e?.target?.value)}
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center justify-end gap-3 pt-4 border-t border-border">
          <Button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-foreground bg-muted hover:bg-muted/80 rounded-lg"
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Salvando...
              </>
            ) : (
              <>
                <Save className="h-4 w-4" />
                {template ? 'Atualizar' : 'Criar'} Template
              </>
            )}
          </Button>
        </div>
      </form>
    </ModalContainer>
  );
};

export default TemplateFormModal;